import axios from "axios";
import { useEffect, useState } from "react";
import { FaUserEdit } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function EditDriver() {
   const navigate = useNavigate();
   const { id } = useParams();

   const [form, setForm] = useState({
      accountNumber: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dob: "",
      country: "",
      address: "",
      city: "",
      borough: "",
      state: "NY",
      zip: "",
      payment_method: "",
      paymentContact: "",
      licenseNumber: "",
      licensePlate: "",
      vehicleBrand: "",
      vehicleModel: "",
      vehicleYear: "",
      vehicleColor: "",
   });

   const [originalArea, setOriginalArea] = useState({
      borough: "",
      zip: "",
      state: "NY"
   });

   const [areaStatus, setAreaStatus] = useState({ message: '', valid: false });

   useEffect(() => {
      const fetchDriver = async () => {
         try {
            const res = await fetch(`/auth/update-redem/${id}`);
            const data = await res.json();
            if (res.ok) {
               setForm({
                  accountNumber: data.accountNumber || "",
                  firstName: data.first_name || "",
                  lastName: data.last_name || "",
                  email: data.email || "",
                  phone: data.phone || "",
                  dob: data.dob ? data.dob.split("T")[0] : "",
                  country: data.country || "",
                  address: data.address || "",
                  city: data.city || "",
                  borough: data.borough || "",
                  state: data.state || "NY",
                  zip: data.zip || "",
                  payment_method: data.payment_method || "",
                  paymentContact: data.paymentContact || "",
                  licenseNumber: data.license_number || "",
                  licensePlate: data.license_plate || "",
                  vehicleBrand: data.vehicle_brand || "",
                  vehicleModel: data.vehicle_model || "",
                  vehicleYear: data.vehicle_year || "",
                  vehicleColor: data.vehicle_color || "",
               });
               setOriginalArea({
                  borough: data.borough || "",
                  zip: data.zip || "",
                  state: data.state || "NY"
               });

            } else {
               toast.error(data.message || "Failed to fetch driver.");
            }
         } catch (err) {
            console.error("Error fetching driver:", err);
            toast.error("Error fetching driver data.");
         }
      };

      fetchDriver();
   }, [id]);

   const handleChange = (e) => {
      const { id, value } = e.target;
      setForm((prev) => ({ ...prev, [id]: value }));
      if (["borough", "zip", "state"].includes(id)) {
         setAreaStatus({ message: "", valid: false });
      }
   };

   const handleZipCheck = async () => {
      try {
         const res = await axios.post("/auth/check-service-area", {
            state: form.state,
            borough: form.borough,
            zipcode: form.zip,
         });
         setAreaStatus({ message: res.data.message, valid: res.data.available });
         toast.success(res.data.message);
      } catch (err) {
         const msg = err.response?.data?.message || "Error checking ZIP availability.";
         setAreaStatus({ message: msg, valid: false });
         toast.error(msg);
      }
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      if (areaChanged && !areaStatus.valid) {
         toast.error('Please check ZIP availability before submitting.');
         return;
      }

      try {
         const res = await fetch(`/auth/update-redem/${id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(form)
         });

         const result = await res.json();

         if (res.ok) {
            toast.success("Driver updated successfully.");
            setTimeout(() => navigate("/redem-driverlist"), 2500);
         } else {
            toast.error(result.message || "Failed to update Driver.");
         }
      } catch (err) {
         console.error("Update failed:", err);
         toast.error("Update failed. Please try again.");
      }
   };

   const areaChanged =
      form.borough !== originalArea.borough ||
      form.zip !== originalArea.zip ||
      form.state !== originalArea.state;


   return (
      <section className="px-4 sm:px-6 lg:px-8 py-8">
         <div className="bg-white max-w-4xl mx-auto p-6 sm:p-8 rounded-2xl shadow-lg">
            <h1 className="text-3xl font-bold text-center text-green-700 mb-8 flex items-center justify-center gap-2">
               <FaUserEdit className="text-3xl" />
               Edit Driver
            </h1>

            <form className="space-y-6" onSubmit={handleSubmit}>
               <div>
                  <label className="block font-medium mb-1">Account Number <span className="text-red-500">*</span></label>
                  <input type="text" id="accountNumber" value={form.accountNumber} readOnly className="w-full p-3 rounded-md bg-gray-100 text-gray-500 border border-gray-300 cursor-not-allowed" />
               </div>

               <div className="grid gap-4 sm:grid-cols-2">
                  <Input id="firstName" label="First Name *" required value={form.firstName} onChange={handleChange} className="bg-lime-50" />
                  <Input id="lastName" label="Last Name *" required value={form.lastName} onChange={handleChange} className="bg-lime-50" />
               </div>

               <div className="grid gap-4 sm:grid-cols-2">
                  <Input id="email" label="Email *" type="email" required value={form.email} onChange={handleChange} className="bg-lime-50" />
                  <Input id="phone" label="Phone Number *" required value={form.phone} onChange={handleChange} className="bg-lime-50" />
               </div>

               <div className="grid gap-4 sm:grid-cols-2">
                  <Input id="dob" label="Date of Birth" type="date" value={form.dob} onChange={handleChange} className="bg-lime-50" />
                  <Select id="country" label="Country" value={form.country} onChange={handleChange} className="bg-lime-50" options={["India", "USA", "UK"]} />
               </div>

               <Input id="address" label="Street Address" value={form.address} onChange={handleChange} className="bg-lime-50" />

               <div className="grid gap-4 sm:grid-cols-2">
                  <Input id="city" label="City" value={form.city} onChange={handleChange} className="bg-lime-50" />
                  <Select id="borough" label="Borough" value={form.borough} onChange={handleChange} className="bg-lime-50" options={["Bronx", "Queens", "Manhattan", "Brooklyn", "Staten Island"]} />
               </div>

               <div className="grid gap-4 sm:grid-cols-3 items-end">
                  <Input id="state" label="State" readOnly value={form.state} onChange={handleChange} className="bg-gray-100 text-gray-500 cursor-not-allowed" />
                  <Input id="zip" label="ZIP" value={form.zip} onChange={handleChange} className="bg-lime-50" />
                  <div>
                     <label className="block invisible mb-1">Check</label>
                     <button
                        type="button"
                        onClick={handleZipCheck}
                        disabled={!areaChanged}
                        className={`w-full h-[42px] px-4 rounded-md text-white 
                           ${areaChanged ? "bg-[#54BE73] hover:bg-green-700" : "bg-gray-300 cursor-not-allowed"}`}
                     >
                        Check Availability
                     </button>
                  </div>
               </div>

               {areaStatus.message && (
                  <div className={`text-sm ${areaStatus.valid ? "text-green-600" : "text-red-600"}`}>
                     {areaStatus.message}
                  </div>
               )}

               <div className="grid gap-4 sm:grid-cols-2">
                  <Input id="licenseNumber" label="Driver License Number" value={form.licenseNumber} onChange={handleChange} />
                  <Input id="licensePlate" label="License Plate Number" value={form.licensePlate} onChange={handleChange} />
               </div>

               <div className="grid gap-4 sm:grid-cols-2">
                  <Input id="vehicleBrand" label="Vehicle Brand" value={form.vehicleBrand} onChange={handleChange} />
                  <Input id="vehicleModel" label="Vehicle Model" value={form.vehicleModel} onChange={handleChange} />
               </div>

               <div className="grid gap-4 sm:grid-cols-2">
                  <Input id="vehicleYear" label="Vehicle Year" value={form.vehicleYear} onChange={handleChange} />
                  <Input id="vehicleColor" label="Vehicle Color" value={form.vehicleColor} onChange={handleChange} />
               </div>

               <Select id="payment_method" label="Payment Method" value={form.payment_method} onChange={handleChange} options={["Cash", "Ev-voucher", "Fuel-Voucher", "Stock-credit", "Check by Mail", "ACH", "PayPal"]} />

               <Input id="paymentContact" label="Email or Mobile for Payment" value={form.paymentContact} onChange={handleChange} />

               <div className="flex justify-end gap-4 pt-2">
                  <button type="button" onClick={() => navigate("/redem-driverlist")} className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300">Cancel</button>
                  <button type="submit" className="px-6 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700">Update</button>
               </div>
            </form>
            <ToastContainer position="top-right" autoClose={3000} />
         </div>
      </section>
   );
}

function Input({ id, label, type = "text", required = false, value, onChange, readOnly = false, className = "" }) {
   return (
      <div>
         <label htmlFor={id} className="block mb-1 font-medium">{label}</label>
         <input
            id={id}
            name={id}
            type={type}
            required={required}
            value={value}
            onChange={onChange}
            readOnly={readOnly}
            className={`w-full rounded-lg p-3 ring-1 ring-gray-300 focus:outline-none focus:ring-2 focus:ring-green-600 ${className}`}
         />
      </div>
   );
}

function Select({ id, label, value, onChange, options = [], className = "" }) {
   return (
      <div>
         <label htmlFor={id} className="block mb-1 font-medium">{label}</label>
         <select
            id={id}
            name={id}
            value={value}
            onChange={onChange}
            className={`w-full rounded-lg p-3 ring-1 ring-gray-300 focus:outline-none focus:ring-2 focus:ring-green-600 ${className}`}
         >
            <option value="">Select {label}</option>
            {options.map((opt) => (

               <option key={opt} value={opt}>{opt}</option>
            ))}
         </select>
      </div>
   );
}
